import { Component, Input, OnChanges } from '@angular/core';
import { DeviceFactory } from '../../site-dashboard/device/device-factory/device-factory';
import { TranslateService } from '@ngx-translate/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { CommonService } from 'src/app/shared/services/common.service';
import { ALL_PERIODS, ALL_YEARS, PerSiteChartsService } from 'src/app/shared/services/per-site-charts.service';

interface PerSiteMetric {
	/** field on the per-site row holding the value */
	field: string;
	titleKey: string;
	unitKey: string;
}

interface PerSiteChart {
	title: string;
	columnNames: any[];
	rows: any[][];
	config: any;
	legend: { text: string, color: string }[];
	printSectionId: string;
	/** ST-2614: the chart's width in % of its container - past 100 it scrolls sideways */
	widthPercent: number;
}

/**
 * ST-2448: "Batteries Usage Data" - one bar per site for the selected period, replacing the
 * customer-wide period bars this chart used to draw. The charts, filters and tooltips all
 * come out of PerSiteChartsService so every per-site chart on the page behaves the same.
 */
@Component({
	selector: 'app-battlink-energy-utilization-charts',
	templateUrl: './battlink-energy-utilization-charts.component.html',
	styleUrls: ['./battlink-energy-utilization-charts.component.css']
})
export class BattlinkEnergyUtilizationChartsComponent implements OnChanges {
	@Input() deviceType?: number = DeviceFactory.types.battlink;
	/**
	 * perSite[deviceType][workKey][year][period] from the backend. Untyped because it is
	 * the raw payload, with no shared model in the app yet.
	 */
	@Input() perSiteData: any = {};
	/** perSiteMeta[deviceType][siteId] = { siteName, installYear }; untyped for the same reason */
	@Input() perSiteMeta: any = {};
	@Input() sites: any[] = [];
	@Input() siteInfo: any = {};

	readonly deviceTypeKey = 'battlink';

	/** decimals the tooltip shows, and the CSV export uses the same (ST-2595) */
	readonly valueDecimals = 0;

	readonly metrics: PerSiteMetric[] = [
		{ field: 'total_battery_charge_ah', titleKey: 'customer.charts.total_battery_charge_ah', unitKey: 'customer.charts.unit_ah' },
		{ field: 'batteries_billed_kwhr', titleKey: 'customer.charts.total_charge_kwh', unitKey: 'customer.charts.unit_kwh' },
		{ field: 'total_battery_inuse_ah', titleKey: 'customer.charts.total_battery_inuse_ah', unitKey: 'customer.charts.unit_ah' },
	];

	monthlyQuarterlyPeriods = [
		{ id: 'quarter', durations: this.translateService.instant('g.quarterly') },
		{ id: 'months', durations: this.translateService.instant('g.monthly') }
	];
	selectedDuration = 'months';

	selectedYear = '';
	selectedPeriod = ALL_PERIODS;
	selectedSites = [];

	ddList = {
		years: [],
		periods: [],
	};

	/** built per metric, in the same order as `metrics` */
	charts: PerSiteChart[] = [];
	/** every chart in the section shares one legend, shown under the filters */
	chartLegend: { text: string, color: string }[] = [];
	showChart = false;

	/**
	 * Held as a field, not a getter. The print header binds to it, and a getter would
	 * rebuild the array on every change-detection pass - which means on every mouse
	 * move over a chart or a dropdown, several translate lookups at a time.
	 */
	printFilters: { label: string, value: string }[] = [];

	constructor(
		private translateService: TranslateService,
		public common: CommonService,
		private perSiteChartsService: PerSiteChartsService,
	) { }

	ngOnChanges() {
		this.buildCharts();
	}

	get workKey(): string {
		return this.selectedDuration == 'quarter' ? 'quarters' : 'months';
	}

	get title(): string {
		return this.translateService.instant('customer.charts.batteries_usage_data');
	}

	/** All Years puts years on the x-axis, so the two period filters have nothing to say */
	get isAllYears(): boolean {
		return this.perSiteChartsService.isAllYears(this.selectedYear);
	}

	get isBucketAxis(): boolean {
		return this.perSiteChartsService.isBucketAxis(this.selectedYear, this.selectedPeriod);
	}

	get allowedSiteIds() {
		if (this.selectedSites?.length)
			return this.selectedSites;

		return (this.sites || []).map(site => site.id);
	}

	get periodDisplay(): string {
		return this.perSiteChartsService.getPeriodDisplay(this.selectedPeriod, this.selectedYear, this.workKey);
	}

	/** rebuilt only when the filters change; the template binds to the cached field */
	buildPrintFilters(): { label: string, value: string }[] {
		const filters = [
			{
				label: 'g.year',
				value: this.common.getOptionLabel(this.ddList.years, this.selectedYear, { valueKey: 'id', labelKey: 'label' }),
			},
			{
				label: 'site.sites',
				value: this.common.getOptionLabel(
					this.sites, this.selectedSites,
					{ valueKey: 'id', labelKey: 'name', fallbackKey: 'site.all_sites' }
				),
			},
		];

		if (this.isAllYears)
			return filters;

		filters.push({
			label: 'g.durations',
			value: this.common.getOptionLabel(
				this.monthlyQuarterlyPeriods, this.selectedDuration,
				{ valueKey: 'id', labelKey: 'durations' }
			),
		});

		filters.push({ label: 'customer.charts.period', value: this.periodDisplay });

		return filters;
	}

	onFilterChange() {
		this.buildCharts();
	}

	buildCharts() {
		this.ddList.years = this.perSiteChartsService.getYearOptions(
			this.perSiteData, this.deviceTypeKey, this.workKey,
			{ meta: this.perSiteMeta, siteIds: this.allowedSiteIds }
		);

		if (!this.ddList.years.some(year => year.id == this.selectedYear))
			this.selectedYear = this.ddList.years[1]?.id || ALL_YEARS;

		this.ddList.periods = this.perSiteChartsService.getPeriodOptions(
			this.perSiteData, this.deviceTypeKey, this.workKey, this.selectedYear
		);

		// All Months / All Quarters is the default, and the fallback whenever the selected
		// period is not in the new year's list
		if (!this.ddList.periods.some(period => period.id == this.selectedPeriod))
			this.selectedPeriod = ALL_PERIODS;

		const periodLabel = this.periodDisplay;
		const bucketAxis = this.isBucketAxis;

		const buckets = bucketAxis
			? this.perSiteChartsService.getBuckets(
				this.perSiteData, this.deviceTypeKey, this.workKey, this.selectedYear, this.selectedPeriod
			)
			: [];

		const rows = bucketAxis
			? []
			: this.perSiteChartsService.getRows(
				this.perSiteData, this.deviceTypeKey, this.workKey, this.selectedYear, this.selectedPeriod
			);

		this.charts = this.metrics.map(metric => {
			const unit = this.translateService.instant(metric.unitKey);
			const title = this.translateService.instant(metric.titleKey);

			const shared = {
				metricField: metric.field,
				siteIds: this.allowedSiteIds,
				periodLabel,
				unit,
				decimals: this.valueDecimals,
			};

			// energy adds up across periods, so a year is the sum of its months
			const built = bucketAxis
				? this.perSiteChartsService.buildSitesAcrossBuckets(buckets, { ...shared, aggregate: 'sum' })
				: this.perSiteChartsService.buildSingleBarBySite(rows, { ...shared, seriesLabel: title });

			const widthPercent = this.perSiteChartsService.getChartWidthPercent(built);

			return {
				title,
				columnNames: built.columnNames,
				rows: built.rows,
				legend: built.legend,
				widthPercent,
				printSectionId: `batteries-usage-data-${metric.field}`,
				config: {
					legend: 'none',
					// same width policy as the truck charts, so the page does not mix thin and
					// wide bars: fill the slot on a period axis, fixed width for a single period
					bar: { groupWidth: bucketAxis ? '92%' : 30 },
					isStacked: false,
					tooltip: { isHtml: true },
					vAxis: { title: unit },
					...this.perSiteChartsService.getChartWidthOptions(widthPercent),
				},
			};
		});

		this.showChart = this.charts.some(chart => chart.rows.length > 0);
		this.chartLegend = this.charts.find(chart => chart.legend.length)?.legend || [];
		this.printFilters = this.buildPrintFilters();
	}

	exportChart(chart: PerSiteChart) {
		const alphabits = this.common.alphabetCharacters;
		const headersInfo = this.common.getCsvFileHeadersInfo({ customerName: this.siteInfo.name });

		const table = this.perSiteChartsService.buildExportTable(chart, {
			periodLabel: this.periodDisplay,
			bucketAxis: this.isBucketAxis,
			decimals: this.valueDecimals,
		});

		const headerObj = {};
		table.headers.forEach((column, colIdx) => headerObj[alphabits[colIdx]] = column);

		const dataObj: any[] = [...headersInfo, headerObj];

		for (const row of table.rows) {
			const rowObj = {};
			row.forEach((value, colIdx) => rowObj[alphabits[colIdx]] = value);
			dataObj.push(rowObj);
		}

		const period = this.periodDisplay.replace(/\s+/g, '-');
		const fileName = this.common.getCleanFileName(this.siteInfo.name)
			+ '-' + period
			+ '-' + this.common.getCleanFileName(chart.title);

		new AngularCsv(dataObj, fileName);
	}
}
